import * as moment from 'moment';
import * as React from 'react';
import { Redirect, RouteComponentProps } from 'react-router';

import { ModalProps, SpinnerPage } from '@common/components';
import { ContractDetailsViewModel } from '@models/Contract';
import { CustomerViewModel } from '@models/Customer';

import { CustomerPicker } from '../../../../customer/_components/customer-picker';

import * as classes from './contract-transfer.scss';
import { getRoute, RouteDefinitions } from '@common/route-definitions';

export enum ContractTransferComponentPage {
  None,
  ConfirmAndSubmit,
  ContractDetails,
  CustomerPicker
}

export interface ContractTranferConfirmationModalComponentProperties {
  readonly addressLine1: string;
  readonly addressCity: string;
  readonly addressState: string;
  readonly addressZipCode: string;
  readonly contractNumber: string;
  readonly customerName: string;
}

export class ContractTranferConfirmationModalComponent extends React.Component<
  ContractTranferConfirmationModalComponentProperties
> {
  public render(): JSX.Element {
    return (
      <p>
        Congratulations, {this.props.customerName || '—'}. You are the new owner of contract{' '}
        {this.props.contractNumber || '—'} covering the property at {this.props.addressLine1 || '—'},{' '}
        {this.props.addressCity || '—'}, {this.props.addressState || '—'} {this.props.addressZipCode || '—'}.
      </p>
    );
  }
}

export interface ContractTransferComponentProperties extends RouteComponentProps<{ id: number }> {
  readonly close: () => void;
  readonly contract?: ContractDetailsViewModel;
  readonly customer?: CustomerViewModel;
  readonly page: ContractTransferComponentPage;
  readonly shouldShowSuccessModal?: boolean;
  readonly isLoading: boolean;

  readonly getContract: (id: number) => void;
  readonly gotoPage: (page: ContractTransferComponentPage) => void;
  readonly reset: () => void;
  readonly setCustomer: (customer?: CustomerViewModel) => void;
  readonly toggleModal: (modalProps: ModalProps) => void;
  readonly transferContract: (id: number, customerId: number) => void;
}

export class ContractTransferComponent extends React.Component<ContractTransferComponentProperties> {
  public static IsContractTransferEnabled(contract?: ContractDetailsViewModel): boolean {
    if (!contract) {
      return false;
    }

    let hasValue = (foo: any): boolean => {
      if (foo) {
        return true;
      }
      return false;
    };

    return (
      !hasValue(contract.ctcPriceAmount) ||
      (hasValue(contract.paymentTotalAmount) && contract.ctcPriceAmount! - contract.paymentTotalAmount! <= 0)
    );
  }

  private static renderCustomer(customer: CustomerViewModel): JSX.Element {
    return (
      <div className="row">
        <div className="col-md-12 mb-10">
          <div className="row mt-10 mb-10 pl-15">
            <div className="col-md-3">{customer.name || '—'}</div>
            <div className="col-md-3">Phone: {customer.phone || '—'}</div>
            <div className="col-md-5 ellipsis">Email: {customer.emailAddress || '—'}</div>
          </div>
        </div>
      </div>
    );
  }

  public componentWillMount(): void {
    if (!this.props.contract) {
      this.props.getContract(this.props.match.params.id);
    }
  }

  public componentWillUnmount(): void {
    this.props.reset();
  }

  public render(): JSX.Element {
    if (this.props.isLoading) {
      return <SpinnerPage />;
    }
    if (this.props.shouldShowSuccessModal) {
      this.props.toggleModal({
        childProps: {
          addressLine1: this.props.contract!.propertyAddress!.line1,
          addressCity: this.props.contract!.propertyAddress!.city,
          addressState: this.props.contract!.propertyAddress!.state!.abbreviation,
          addressZipCode: this.props.contract!.propertyAddress!.zip,
          contractNumber: this.props.contract!.contractNumber,
          customerName: this.props.customer!.name
        },
        closeCallback: this.props.close,
        component: ContractTranferConfirmationModalComponent,
        hideSave: true,
        title: 'Contract Transfer Confirmation',
        isSmall: true
      });
    } else if (this.props.page === ContractTransferComponentPage.None) {
      return (
        <Redirect push={true} to={getRoute(RouteDefinitions.contract.Emails, { id: +this.props.match.params.id })} />
      );
    }

    let content: JSX.Element = <></>;
    switch (this.props.page) {
      case ContractTransferComponentPage.ConfirmAndSubmit:
        content = this.renderConfirmAndSubmit();
        break;
      case ContractTransferComponentPage.ContractDetails:
        content = this.renderContractDetails();
        break;
      case ContractTransferComponentPage.CustomerPicker:
      default:
        content = this.renderCustomerPicker();
        break;
    }
    /*
        if (this.props.contract) {
            content = this.renderContractDetails();
        }
        */
    return content;
  }

  private renderConfirmAndSubmit(): JSX.Element {
    return (
      <>
        <h2>Confirm and Submit</h2>
        <p>
          You are about to transfer ownership of the following contract on behalf of {this.props.contract!.buyer!.name}.
        </p>
        <div className={classes.contractTransferInfo}>
          <div className={classes.container}>
            <div className="row">
              <div className="col-sm-6">
                <label>Contract Number: </label>
                <h5>{this.props.contract!.code}</h5>
              </div>
              <div className="col-sm-6">
                <label>Expiration Date: </label>
                <h5>{moment(this.props.contract!.termEnd).format('L') || '—'}</h5>
              </div>
            </div>
          </div>
          <br />
          <p>Upon submission, the contract ownership shall be transferred to {this.props.customer!.name}.</p>
          <p>If you wish to proceed, please click submit.</p>
          <div className="row end-sm">
            <button className="btn btn-secondary" onClick={this.props.history.goBack} type="button">
              Cancel
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => this.props.gotoPage(ContractTransferComponentPage.ContractDetails)}
              type="button"
            >
              Back
            </button>
            <button
              className="btn btn-primary"
              onClick={() => this.props.transferContract(this.props.contract!.id, this.props.customer!.id)}
              type="button"
            >
              Submit
            </button>
          </div>
        </div>
      </>
    );
  }

  private renderContractDetails(): JSX.Element {
    let totalPrice: number = this.props.contract!.coverageOptions
      ? this.props.contract!.coverageOptions.map(co => co.price).reduce((sum, current) => sum + current)
      : 0;

    let coverageOption1: string =
      this.props.contract!.coverageOptions &&
      this.props.contract!.coverageOptions[0] &&
      this.props.contract!.coverageOptions[0].value;
    let coverageOption2: string =
      this.props.contract!.coverageOptions &&
      this.props.contract!.coverageOptions[1] &&
      this.props.contract!.coverageOptions[1].value;

    return (
      <>
        <h2>Contract Details</h2>
        <div className={classes.contractTransferInfo}>
          <div className={classes.container}>
            <div className="row">
              <div className="col-sm-6">
                <div>
                  <label>Number: </label>
                  <h5>{this.props.contract!.code || '—'}</h5>
                </div>
                <div>
                  <label>Address: </label>
                  <h5>{this.props.contract!.propertyAddress!.line1 || '—'}</h5>
                  <h5>
                    {this.props.contract!.propertyAddress!.city || '—'}
                    ,&nbsp;
                    {this.props.contract!.propertyAddress!.state!.abbreviation || '—'}
                    &nbsp;
                    {this.props.contract!.propertyAddress!.zip || '—'}
                  </h5>
                </div>
                <div>
                  <label>Expiration Date: </label>
                  <h5>{moment(this.props.contract!.termEnd).format('L') || '—'}</h5>
                </div>
              </div>
              <div className="col-sm-6">
                <div>
                  <label>Product: </label>
                  <h5>{this.props.contract!.productName || '—'}</h5>
                </div>
                <div>
                  <label>Coverage Option 1: </label>
                  <h5>{coverageOption1 || '—'}</h5>
                </div>
                <div>
                  <label>Coverage Option 2: </label>
                  <h5>{coverageOption2 || '—'}</h5>
                </div>
                <div>
                  <label>Total Price: </label>
                  <h5>${totalPrice.toFixed(2)}</h5>
                </div>
              </div>
            </div>
          </div>
          <div className="row end-sm">
            <button className="btn btn-secondary" onClick={this.props.history.goBack} type="button">
              Cancel
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => this.props.gotoPage(ContractTransferComponentPage.CustomerPicker)}
              type="button"
            >
              Back
            </button>
            <button
              className="btn btn-primary"
              onClick={() => this.props.gotoPage(ContractTransferComponentPage.ConfirmAndSubmit)}
              type="button"
            >
              Next
            </button>
          </div>
        </div>
      </>
    );
  }

  private renderCustomerPicker(): JSX.Element {
    const isNextButtonDisabled: boolean = !(
      this.props.customer && ContractTransferComponent.IsContractTransferEnabled(this.props.contract!)
    );

    return (
      <>
        <h2>Transfer Contract</h2>
        <form>
          <CustomerPicker
            onPick={this.props.setCustomer}
            toggleModal={this.props.toggleModal}
            placeholder="Search and Select Customer..."
          />
          {this.props.customer &&
            this.props.customer.name &&
            ContractTransferComponent.renderCustomer(this.props.customer)}
        </form>
        <div className="row end-sm">
          <button className="btn btn-secondary" onClick={this.props.history.goBack} type="button">
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={isNextButtonDisabled}
            onClick={() => this.props.gotoPage(ContractTransferComponentPage.ContractDetails)}
            type="button"
          >
            Next
          </button>
        </div>
      </>
    );
  }
}



// WEBPACK FOOTER //
// ./src/contract/_components/contract-transfer/_components/contract-transfer.component.tsx